import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuthStore } from "@/store"
import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Shield, Loader2, Eye, EyeOff } from "lucide-react"

export default function LoginPage() {
  const navigate = useNavigate()
  const setToken = useAuthStore((s) => s.setToken)
  const setUser = useAuthStore((s) => s.setUser)
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated)

  const [needsSetup, setNeedsSetup] = useState(null)
  const [isSetupMode, setIsSetupMode] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [form, setForm] = useState({
    username: "",
    password: "",
    confirmPassword: "",
  })

  useEffect(() => {
    checkSetup()
  }, [])

  const checkSetup = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || "/api"}/auth/setup`)
      const data = await res.json()
      if (data.success && data.needsSetup) {
        setNeedsSetup(true)
        setIsSetupMode(true)
      } else {
        setNeedsSetup(false)
      }
    } catch {
      setNeedsSetup(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      if (isSetupMode) {
        if (form.password !== form.confirmPassword) {
          setError("Passwords do not match")
          setLoading(false)
          return
        }
        if (form.password.length < 8) {
          setError("Password must be at least 8 characters")
          setLoading(false)
          return
        }

        const res = await fetch(`${import.meta.env.VITE_API_URL || "/api"}/auth/setup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: form.username,
            password: form.password,
            confirmPassword: form.confirmPassword,
          }),
        })
        const data = await res.json()

        if (data.success && data.token) {
          api.setToken(data.token)
          setToken(data.token)
          setUser(data.user)
          setAuthenticated(true)
          navigate("/")
        } else {
          setError(data.error || "Setup failed")
        }
      } else {
        const res = await api.post("/auth/login", {
          username: form.username,
          password: form.password,
        })

        if (res.success && res.token) {
          api.setToken(res.token)
          setToken(res.token)
          setUser(res.user)
          setAuthenticated(true)
          navigate("/")
        } else {
          setError(res.error || "Login failed")
        }
      }
    } catch (err) {
      setError(err.message || "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  if (needsSetup === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-[hsl(var(--bg-primary))]">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-500))]" />
      </div>
    )
  }

  return (
    <div className="flex h-screen items-center justify-center bg-[hsl(var(--bg-primary))] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-500))] mb-4">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <CardTitle className="text-xl">KouroshShield</CardTitle>
          <CardDescription>
            {isSetupMode
              ? "Create your administrator account"
              : "Sign in to your account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-[hsl(var(--status-error-bg))] p-3 text-sm text-[hsl(var(--status-error))]">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-[hsl(var(--text-primary))]">Username</label>
              <Input
                type="text"
                placeholder="Enter username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[hsl(var(--text-primary))]">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {isSetupMode && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-[hsl(var(--text-primary))]">Confirm Password</label>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  required
                />
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {isSetupMode ? "Create Account" : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
